import { Optional } from "../../support/optional.model";
import { OrderItemStatus } from "../dto/order-item-status.enum";

	/**
	 * @description
	 *  Modelo de un ítem en un pedido, para frontend.
	 *
	 * @see gigot-backend/ViewModels/Order/OrderDto.cs
	 */

export class OrderItem {

	public campaignProductId: number;
	public finalDiscountedPrice: number;
	public finalPrice: number;
	public id: number;
	public imagePath: string;
	public isGift: boolean;
	public isHidden: boolean;
	public isPreviousNoStock: boolean;
	public isReturn: boolean;
	public orderItemStatus: string;
	public paymentCycle: number;
	public price: number;
	public productCode: string;
	public productName: string;
	public profit: number;
	public promotionPrice: number;
	public promotionPriceQuantity: number;
	public quantity: number;
	public rewardId: number;
    public promotionIds: string[];
    public isPromotionDeleteable: boolean;
	public isPoints: boolean;

	/**
	 * @description
	 *	Cantidad total de cuotas para el producto asociado. Esta cantidad se
	 *	sintetiza desde "CampaignProduct.PaymentCycles".
	 */
	public totalPaymentCycle: number;

	/**
	 * @description
	 *	Construye un ítem de pedido con una semántica vacía. Nótese que no es
	 *	lo mismo que construir una selección con valores por defecto. Las
	 *	reglas generales de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Un ítem vacío.
	 */
	public static empty(): OrderItem {
		const orderItem: OrderItem = new OrderItem();
		orderItem.campaignProductId = undefined;
		orderItem.finalDiscountedPrice = undefined;
		orderItem.finalPrice = undefined;
		orderItem.id = undefined;
		orderItem.imagePath = "";
		orderItem.isGift = false;
		orderItem.isHidden = false;
		orderItem.isPreviousNoStock = false;
		orderItem.isReturn = false;
		orderItem.orderItemStatus = "";
		orderItem.paymentCycle = undefined;
		orderItem.price = undefined;
		orderItem.productCode = "";
		orderItem.productName = "";
		orderItem.profit = undefined;
		orderItem.promotionPrice = undefined;
		orderItem.promotionPriceQuantity = undefined;
		orderItem.quantity = undefined;
		orderItem.rewardId = undefined;
		orderItem.totalPaymentCycle = undefined;
		orderItem.isPoints = false;
		return orderItem;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrderItem
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrderItem: OrderItem): OrderItem {
		const orderItem: OrderItem = OrderItem.empty();
		orderItem.campaignProductId = anemicOrderItem.campaignProductId;
		orderItem.finalDiscountedPrice = anemicOrderItem.finalDiscountedPrice;
		orderItem.finalPrice = anemicOrderItem.finalPrice;
		orderItem.id = anemicOrderItem.id;
		orderItem.imagePath = anemicOrderItem.imagePath;
		orderItem.isGift = anemicOrderItem.isGift;
		orderItem.isHidden = anemicOrderItem.isHidden;
		orderItem.isPreviousNoStock = anemicOrderItem.isPreviousNoStock;
		orderItem.isReturn = anemicOrderItem.isReturn;
		orderItem.orderItemStatus = anemicOrderItem.orderItemStatus;
		orderItem.paymentCycle = anemicOrderItem.paymentCycle;
		orderItem.price = anemicOrderItem.price;
		orderItem.productCode = anemicOrderItem.productCode;
		orderItem.productName = anemicOrderItem.productName;
		orderItem.profit = anemicOrderItem.profit;
		orderItem.promotionPrice = anemicOrderItem.promotionPrice;
		orderItem.promotionPriceQuantity = anemicOrderItem.promotionPriceQuantity;
		orderItem.quantity = anemicOrderItem.quantity;
		orderItem.rewardId = anemicOrderItem.rewardId;
		orderItem.totalPaymentCycle = anemicOrderItem.totalPaymentCycle;
		orderItem.isPoints = anemicOrderItem.isPoints;
		return orderItem;
	}

	/**
	 * @description
	 *	Indica la existencia de una ruta hacia la imagen del producto asociado
	 *	en este ítem, útil para determinar la necesidad de mostrar una imagen
	 *	por defecto en la GUI.
	 *
	 * @returns
	 *	Verdadero si existe una imagen definida para este ítem, falso de otro
	 *	modo.
	 */
	public hasImage(): boolean {
		return Optional.of(this.imagePath)
			.filter(path => path.trim() !== "")
			.isPresent();
	}

	/**
	 * @description
	 *	Determina si este ítem debe pagarse en cuotas.
	 *
	 * @returns
	 *	Verdadero si debe pagarse en 2 o más cuotas, falso si requiere un
	 *	único pago.
	 */
	public hasPaymentCycle(): boolean {
		return 1 < this.totalPaymentCycle && !this.isReturn;
	}

	/**
	 * @description
	 *	Determina si este ítem posee stock o no.
	 *
	 * @returns
	 *	Verdadero si posee stock, falso de otro modo.
	 */
	public hasStock(): boolean {
		return this.orderItemStatus !== OrderItemStatus.NoStock;
	}

	/**
	 * @description
	 *	Determina si este ítem se encuentra en su último ciclo de pago, es
	 *	decir, en su última cuota.
	 *
	 * @returns
	 *	Verdadero si el ítem se encuentra en su última cuota, falso de otro
	 *	modo. Si el ítem no es en cuotas, entonces siempre se encuentra en su
	 *	último ciclo y por lo tanto se devuelve verdadero.
	 */
	public inLastPaymentCycle(): boolean {
		return this.paymentCycle === this.totalPaymentCycle;
	}

	/**
	 * @description
	 *	Indica si este ítem no tiene una cantidad especificada.
	 *
	 * @returns
	 *	Verdadero si la cantidad y la cantidad de promoción es igual que 0,
	 *	falso de otro modo.
	 */
	public isEmpty(): boolean {
		return this.quantity === 0 && this.promotionPriceQuantity === 0;
	}

	/**
	 * @description
	 *	Computa el precio de folleto de este ítem, considerando los precios y
	 *	cantidades de promoción. No tiene en cuenta el ciclo de pago actual.
	 *
	 * @returns
	 *	El precio de folleto total para este ítem.
	 */
	public totalBrochurePrice(): number {
		return (this.quantity - this.promotionPriceQuantity) * this.price
			+ this.promotionPriceQuantity * this.promotionPrice;
	}

	/**
	 * @description
	 *	Computa el precio facturado de este ítem, considerando los precios y
	 *	cantidades de promoción. Tiene en cuenta el ciclo de pago actual, en
	 *	cuyo caso el monto final será el monto individual de la cuota actual
	 *	multiplicado por la cantidad de cuotas pagadas.
	 *
	 * @returns
	 *	El precio facturado total para este ítem.
	 */
	public totalInvoicePrice(): number {
		return this.paymentCycle *
			((this.quantity - this.promotionPriceQuantity) * this.finalPrice
			+ this.promotionPriceQuantity * this.finalDiscountedPrice);
	}
}
